import { useMemo } from 'react';

import { useUserStore } from '../model/user.store';

/**
 * Thrown by `getCurrentOrganizationId` when the user store has not yet been
 * populated. Named so call sites and error reporters can disambiguate this
 * from generic `Error` instances (and so tests can assert the type).
 */
export class MissingOrganizationError extends Error {
  // eslint-disable-next-line i18next/no-literal-string -- developer diagnostic held in `authStore.error`; the login flow redirects rather than rendering it
  constructor(message = 'Organization ID not found in user store') {
    super(message);
    this.name = 'MissingOrganizationError';
  }
}

/** Returns the full signed-in user object (or `null` before sign-in resolves). */
export const useCurrentUser = () => useUserStore((s) => s.user);

/** Returns the signed-in user's id (or `undefined` before sign-in resolves). */
export const useCurrentUserId = () => useUserStore((s) => s.user?.id);

/** Returns the signed-in user's workspaces (empty array before sign-in resolves). */
export const useUserWorkspaces = () => useUserStore((s) => s.user?.workspaces ?? []);

/**
 * Returns the signed-in user's organization name (or `undefined` before
 * sign-in resolves).
 *
 * Reactive React hook — use in render. For imperative reads from outside
 * React (e.g. mutation handlers), use `getCurrentOrganizationId` for the id;
 * a non-hook getter for the name is intentionally not exposed because no
 * call site needs it imperatively today.
 */
export const useCurrentOrganizationName = () => useUserStore((s) => s.user?.organization.name);

/** Undefined before sign-in resolves, unlike the getter below, which throws. */
export const useCurrentOrganizationId = () => useUserStore((s) => s.user?.organization.id);

/**
 * The signed-in user's workspaces, never any belonging to another organization.
 *
 * `/auth/me` answers with one `organization` and a flat `workspaces` list, and in practice the
 * list omits `organizationId` per entry even though the SDK type declares it. So a workspace
 * without it is kept: the session already scopes the response to one organization, and dropping
 * every entry that cannot prove its own organization empties the list instead of protecting it.
 * The comparison still runs whenever the field is there, which is what keeps a multi-organization
 * response from leaking one organization's workspace names into another's picker.
 */
export const useCurrentOrganizationWorkspaces = () => {
  const workspaces = useUserWorkspaces();
  const organizationId = useCurrentOrganizationId();

  // Filtered outside the store selector: returning a fresh array from one makes every store read
  // look like a change, since the default comparison is by identity.
  return useMemo(
    () =>
      workspaces.filter(
        (workspace) => !workspace.organizationId || workspace.organizationId === organizationId,
      ),
    [workspaces, organizationId],
  );
};

/**
 * Non-hook getter for the current organization id.
 *
 * Throws `MissingOrganizationError` when the user store has not been
 * populated — call sites assume a signed-in user is present (e.g. mutation
 * hooks executed after init). Use `useCurrentUser()` and check for `null`
 * if you need a non-throwing read.
 */
export const getCurrentOrganizationId = (): string => {
  const orgId = useUserStore.getState().user?.organization.id;
  if (!orgId) {
    throw new MissingOrganizationError();
  }
  return orgId;
};
